import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { useFirestore } from "reactfire";
import { firestore } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

export type YoucatQuestionType = "mcq" | "short_answer" | "long_answer";

export type YoucatMonthlyQuestion = {
  id: string;
  question: string;
  type: YoucatQuestionType;
  required: boolean;
  options: string[];
};

type YoucatMonthlyFormConfig = {
  docId: string;
  monthKey: string;
  monthLabel: string;
  questions: YoucatMonthlyQuestion[];
  updatedAt?: string;
};

const EMPTY_QUESTION: YoucatMonthlyQuestion = {
  id: "",
  question: "",
  type: "short_answer",
  required: true,
  options: ["Option 1", "Option 2"],
};

export const YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION =
  "youcatMonthlyFormConfigs";

function getMonthLabel(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);

  if (!year || !month) {
    return monthKey;
  }

  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export default function YoucatMonthlyFormTab() {
  const firestoreInstance = useFirestore();
  const { showSnackbar } = useFeedback();
  const [configs, setConfigs] = useState<YoucatMonthlyFormConfig[]>([]);
  const [monthKey, setMonthKey] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [questions, setQuestions] = useState<YoucatMonthlyQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const activeConfig = useMemo(
    () => configs.find((config) => config.monthKey === monthKey),
    [configs, monthKey],
  );

  async function loadConfigs() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(
        query(
          collection(firestoreInstance, YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION),
          orderBy("monthKey", "desc"),
        ),
      );
      setConfigs(
        snapshot.docs.map((configDoc) => ({
          docId: configDoc.id,
          ...(configDoc.data() as Omit<YoucatMonthlyFormConfig, "docId">),
        })),
      );
    } catch (error) {
      console.error("Failed to load YouCat monthly forms", error);
      showSnackbar("Failed to load YouCat monthly forms");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeConfig?.questions?.length) {
      setQuestions(activeConfig.questions);
      return;
    }

    setQuestions([]);
  }, [activeConfig]);

  function handleAddQuestion() {
    setQuestions((prev) => [
      ...prev,
      {
        ...EMPTY_QUESTION,
        id: `${Date.now()}-${prev.length + 1}`,
      },
    ]);
  }

  async function handleSave() {
    const validQuestions = questions.filter((item) => item.question.trim());

    try {
      setIsSaving(true);
      await setDoc(
        doc(firestore, YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION, monthKey),
        {
          monthKey,
          monthLabel: getMonthLabel(monthKey),
          questions: validQuestions.map((question, index) => ({
            ...question,
            id: question.id || `${monthKey}-${index + 1}`,
            question: question.question.trim(),
            options:
              question.type === "mcq"
                ? question.options.map((item) => item.trim()).filter(Boolean)
                : [],
          })),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
      showSnackbar("YouCat monthly form saved successfully");
      loadConfigs();
    } catch (error) {
      console.error("Failed to save YouCat monthly form", error);
      showSnackbar("Failed to save YouCat monthly form");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteMonthForm(targetMonthKey: string) {
    if (!window.confirm(`Delete the monthly form for ${getMonthLabel(targetMonthKey)}?`)) {
      return;
    }

    try {
      await deleteDoc(
        doc(firestore, YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION, targetMonthKey),
      );

      if (targetMonthKey === monthKey) {
        setQuestions([]);
      }

      showSnackbar("YouCat monthly form deleted successfully");
      loadConfigs();
    } catch (error) {
      console.error("Failed to delete YouCat monthly form", error);
      showSnackbar("Failed to delete YouCat monthly form");
    }
  }

  return (
    <Box p={3}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Monthly Question Form
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Add or change the questions that should be active automatically for the
        current month in Indian Standard Time.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="month"
            label="Month"
            value={monthKey}
            onChange={(event) => setMonthKey(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button startIcon={<Add />} variant="outlined" onClick={handleAddQuestion}>
              Add Question
            </Button>
            {activeConfig ? (
              <Button
                startIcon={<Delete />}
                color="error"
                variant="outlined"
                onClick={() => handleDeleteMonthForm(monthKey)}
              >
                Delete This Monthly Form
              </Button>
            ) : null}
          </Stack>
        </Grid>
      </Grid>

      <Stack spacing={2} mt={3}>
        {questions.map((question, index) => (
          <Card key={question.id || index} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={`Question ${index + 1}`}
                    value={question.question}
                    onChange={(event) =>
                      setQuestions((prev) =>
                        prev.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, question: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Type"
                    value={question.type}
                    onChange={(event) =>
                      setQuestions((prev) =>
                        prev.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                type: event.target.value as YoucatQuestionType,
                              }
                            : item,
                        ),
                      )
                    }
                  >
                    <MenuItem value="mcq">MCQ</MenuItem>
                    <MenuItem value="short_answer">Short Answer</MenuItem>
                    <MenuItem value="long_answer">Long Answer</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Required"
                    value={question.required ? "yes" : "no"}
                    onChange={(event) =>
                      setQuestions((prev) =>
                        prev.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, required: event.target.value === "yes" }
                            : item,
                        ),
                      )
                    }
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      if (!window.confirm("Remove this question from the monthly form?")) {
                        return;
                      }

                      setQuestions((prev) =>
                        prev.filter((_, itemIndex) => itemIndex !== index),
                      );
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
                {question.type === "mcq" ? (
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      {question.options.map((option, optionIndex) => (
                        <TextField
                          key={`${question.id}-${optionIndex}`}
                          fullWidth
                          label={`Option ${optionIndex + 1}`}
                          value={option}
                          onChange={(event) =>
                            setQuestions((prev) =>
                              prev.map((item, itemIndex) =>
                                itemIndex === index
                                  ? {
                                      ...item,
                                      options: item.options.map((itemOption, i) =>
                                        i === optionIndex
                                          ? event.target.value
                                          : itemOption,
                                      ),
                                    }
                                  : item,
                              ),
                            )
                          }
                        />
                      ))}
                      <Button
                        variant="text"
                        onClick={() =>
                          setQuestions((prev) =>
                            prev.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    options: [...item.options, `Option ${item.options.length + 1}`],
                                  }
                                : item,
                            ),
                          )
                        }
                      >
                        Add Option
                      </Button>
                    </Stack>
                  </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Box mt={3}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Monthly Form"}
        </Button>
      </Box>

      <Box mt={5}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Existing Monthly Forms
        </Typography>
        {isLoading ? (
          <Typography>Loading monthly forms...</Typography>
        ) : !configs.length ? (
          <Typography color="text.secondary">
            No monthly forms created yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {configs.map((config) => (
              <Box
                key={config.docId}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
              >
                <Typography>
                  {config.monthLabel}: {config.questions?.length || 0} question
                  {(config.questions?.length || 0) === 1 ? "" : "s"}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => handleDeleteMonthForm(config.monthKey)}
                >
                  Delete
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
